import * as React from "react";
import { Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

export type ConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  /** State what will happen, including anything that cannot be undone. */
  description: React.ReactNode;
  /** Names the action rather than saying "OK", e.g. "Archive project". */
  confirmLabel: string;
  cancelLabel?: string;
  /** `destructive` colours the confirm button and is reserved for data loss. */
  tone?: "default" | "destructive";
  onConfirm: () => void;
  /** Keeps the dialog open and the buttons disabled while the work runs. */
  busy?: boolean;
  busyLabel?: string;
  /** Extra context, such as the dependent records an archive would retain. */
  children?: React.ReactNode;
  /**
   * Called as focus returns to the trigger. Prevent the default and focus
   * something else when the trigger no longer exists, such as a removed row.
   */
  onCloseAutoFocus?: (event: Event) => void;
};

/**
 * The confirmation step for an action that cannot be casually undone.
 *
 * Radix `AlertDialog` moves focus to the dialog, traps it, restores it to the
 * trigger on close, and defaults focus to Cancel, so the destructive choice is
 * never the one a stray Enter press selects.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = "Cancel",
  tone = "default",
  onConfirm,
  busy = false,
  busyLabel,
  children,
  onCloseAutoFocus,
}: ConfirmDialogProps) {
  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        // Dismissing mid-flight would leave the user unsure whether the action
        // ran, so the dialog stays put until the work settles.
        if (busy) return;
        onOpenChange(next);
      }}
    >
      <AlertDialogContent onCloseAutoFocus={onCloseAutoFocus}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>

        {children}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            variant={tone === "destructive" ? "destructive" : "default"}
            disabled={busy}
            onClick={(event) => {
              // The caller closes the dialog once the work resolves.
              event.preventDefault();
              onConfirm();
            }}
          >
            {busy && (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            )}
            {busy ? (busyLabel ?? confirmLabel) : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
